import { useState, useEffect, Fragment } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Search, FileText, RefreshCw, User, Clock, Activity, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { downloadPdf } from '../utils/downloadPdf';
import { fmtDateTime as fmtDateTimeTz } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Audit() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [availableModules, setAvailableModules] = useState([]);
  const [availableActions, setAvailableActions] = useState([]);
  
  const [filters, setFilters] = useState({
    desde: '',
    hasta: '',
    usuario_id: '',
    modulo: '',
    accion: ''
  });
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsRes, usersRes, statsRes, filtersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/audit-logs`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/audit-logs/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/audit-logs/filters`, { withCredentials: true }),
      ]);
      setLogs(logsRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setAvailableModules(filtersRes.data?.modules || []);
      setAvailableActions(filtersRes.data?.actions || []);
    } catch (error) {
      toast.error('Error al cargar datos de auditoría');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.desde) params.append('desde', filters.desde);
      if (filters.hasta) params.append('hasta', filters.hasta);
      if (filters.usuario_id) params.append('usuario_id', filters.usuario_id);
      if (filters.modulo) params.append('modulo', filters.modulo);
      if (filters.accion) params.append('accion', filters.accion);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/audit-logs?${params.toString()}`, 
        { withCredentials: true }
      );
      setLogs(response.data);
      toast.success(`${response.data.length} registros encontrados`);
    } catch (error) {
      toast.error('Error al buscar');
    } finally {
      setLoading(false);
    }
  };
  
  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.desde) params.append('desde', filters.desde);
      if (filters.hasta) params.append('hasta', filters.hasta);
      if (filters.usuario_id) params.append('usuario_id', filters.usuario_id);
      if (filters.modulo) params.append('modulo', filters.modulo);
      
      await downloadPdf(
        `/audit-logs/export/pdf?${params.toString()}`,
        `auditoria_${new Date().toISOString().slice(0,10)}.pdf`,
        'Generando reporte de auditoría...'
      );
    } finally {
      setExporting(false);
    }
  };
  
  const handleClearFilters = () => {
    setFilters({
      desde: '',
      hasta: '',
      usuario_id: '',
      modulo: '',
      accion: ''
    });
  };
  
  const getActionBadge = (action) => {
    const styles = {
      'ACCESO': 'bg-indigo-100 text-indigo-800',
      'CREÓ': 'bg-green-100 text-green-800',
      'EDITÓ': 'bg-amber-100 text-amber-800',
      'ELIMINÓ': 'bg-red-100 text-red-800',
      'APROBÓ': 'bg-green-100 text-green-800',
      'RECHAZÓ': 'bg-red-100 text-red-800',
      'DESPACHÓ': 'bg-teal-100 text-teal-800',
      'RECIBIÓ': 'bg-emerald-100 text-emerald-800',
      'CERRÓ CON SALDO': 'bg-orange-100 text-orange-800',
      'ANULÓ': 'bg-red-100 text-red-800',
      'EJECUTÓ': 'bg-blue-100 text-blue-800',
      'VALORIZÓ': 'bg-purple-100 text-purple-800',
      'FACTURÓ': 'bg-purple-100 text-purple-800',
      'CONSUMIÓ': 'bg-cyan-100 text-cyan-800',
    };
    return <Badge className={styles[action] || 'bg-gray-100 text-gray-800'}>{action}</Badge>;
  };
  
  const formatDate = (dateStr) => (dateStr ? fmtDateTimeTz(dateStr) : '-');
  
  const formatModuleName = (module) => {
    if (!module) return '-';
    return module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  if (loading && logs.length === 0) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  
  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Auditoría</h1>
          <p className="text-muted-foreground">Registro de todas las acciones del sistema</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={handleExportPdf} disabled={exporting}>
            <FileText className={`w-4 h-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
            Exportar PDF
          </Button>
        </div>
      </div>
      
      {/* Estadísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Registros</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_registros?.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Usuario más activo (30d)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">
                  {stats.top_usuarios_30d?.[0]?.user_name || '-'}
                </span>
                {stats.top_usuarios_30d?.[0]?.count && (
                  <Badge variant="outline">{stats.top_usuarios_30d[0].count} acciones</Badge>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Módulo más usado (30d)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">
                  {formatModuleName(stats.por_modulo_30d?.[0]?._id) || '-'}
                </span>
                {stats.por_modulo_30d?.[0]?.count && (
                  <Badge variant="outline">{stats.por_modulo_30d[0].count}</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <Label htmlFor="desde">Desde</Label>
              <Input
                id="desde"
                type="date"
                value={filters.desde}
                onChange={(e) => setFilters({ ...filters, desde: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="hasta">Hasta</Label>
              <Input
                id="hasta"
                type="date"
                value={filters.hasta}
                onChange={(e) => setFilters({ ...filters, hasta: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="usuario">Usuario</Label>
              <select
                id="usuario"
                value={filters.usuario_id}
                onChange={(e) => setFilters({ ...filters, usuario_id: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Todos</option>
                {users.map(u => (
                  <option key={u.user_id} value={u.user_id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="modulo">Módulo</Label>
              <select
                id="modulo"
                value={filters.modulo}
                onChange={(e) => setFilters({ ...filters, modulo: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="audit-filter-modulo"
              >
                <option value="">Todos los módulos</option>
                {availableModules.map(m => (
                  <option key={m} value={m}>{formatModuleName(m)}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="accion">Acción</Label>
              <select
                id="accion"
                value={filters.accion}
                onChange={(e) => setFilters({ ...filters, accion: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="audit-filter-accion"
              >
                <option value="">Todas las acciones</option>
                {availableActions.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleSearch} className="flex-1">
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </Button>
              <Button variant="outline" onClick={handleClearFilters}>
                Limpiar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Tabla de logs */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead className="w-[140px]">Fecha/Hora</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Módulo</TableHead>
                <TableHead>Acción</TableHead>
                <TableHead>Descripción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => {
                const hasExtra = !!(log.details || log.previous_data || log.new_data);
                const isOpen = !!expanded[log.log_id];
                return (
                  <Fragment key={log.log_id}>
                    <TableRow>
                      <TableCell className="p-2 text-center">
                        {hasExtra ? (
                          <button
                            onClick={() => setExpanded(prev => ({ ...prev, [log.log_id]: !isOpen }))}
                            className="text-slate-500 hover:text-slate-900"
                            title={isOpen ? 'Ocultar detalle' : 'Ver detalle'}
                            data-testid={`audit-expand-${log.log_id}`}
                          >
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          {formatDate(log.timestamp)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{log.user_name || log.user_email || '-'}</div>
                        {log.user_email && log.user_name && (
                          <div className="text-xs text-muted-foreground">{log.user_email}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground font-mono">
                        {log.ip_address || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{formatModuleName(log.module)}</Badge>
                      </TableCell>
                      <TableCell>
                        {getActionBadge(log.action)}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <div className="text-sm">{log.description}</div>
                        {log.document_number && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Doc: {log.document_number}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    {hasExtra && isOpen && (
                      <TableRow className="bg-slate-50/70">
                        <TableCell colSpan={7} className="py-3 px-6" data-testid={`audit-detail-${log.log_id}`}>
                          <AuditDetail details={log.details} previous={log.previous_data} next={log.new_data} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
          
          {logs.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay registros de auditoría que coincidan con los filtros.</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      <div className="text-center text-sm text-muted-foreground">
        Mostrando {logs.length} registros
      </div>
    </div>
  );
}

export default Audit;

// -------- Componente auxiliar para desplegar detalles del log --------

function AuditDetail({ details, previous, next }) {
  const isEmpty = (v) => v === null || v === undefined || (typeof v === 'object' && Object.keys(v).length === 0);
  return (
    <div className="space-y-3 text-sm">
      {!isEmpty(details) && (
        <DetailBlock title="Detalle" data={details} />
      )}
      {!isEmpty(previous) && (
        <DetailBlock title="Antes" data={previous} tone="rose" />
      )}
      {!isEmpty(next) && (
        <DetailBlock title="Después" data={next} tone="emerald" />
      )}
    </div>
  );
}

function DetailBlock({ title, data, tone = 'slate' }) {
  const toneClass = {
    slate: 'border-slate-300 bg-white',
    rose: 'border-rose-300 bg-rose-50/70',
    emerald: 'border-emerald-300 bg-emerald-50/70',
  }[tone] || 'border-slate-300 bg-white';
  return (
    <div className={`border rounded-md p-3 ${toneClass}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">{title}</div>
      {renderValue(data)}
    </div>
  );
}

function renderValue(v, keyPrefix = '') {
  if (v === null || v === undefined) return <span className="text-slate-400">—</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-slate-400">(vacío)</span>;
    // Si es array de objetos con misma "forma", renderizar como tabla
    if (typeof v[0] === 'object' && v[0] !== null) {
      const cols = Array.from(new Set(v.flatMap(x => Object.keys(x || {}))));
      return (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-100">
                {cols.map(c => (
                  <th key={c} className="px-2 py-1 text-left border border-slate-200 font-semibold text-slate-700">{humanize(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {v.map((row, i) => (
                <tr key={`${keyPrefix}${i}`} className="odd:bg-white even:bg-slate-50/60">
                  {cols.map(c => (
                    <td key={c} className="px-2 py-1 border border-slate-200 align-top">{formatScalar(row?.[c])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return <ul className="list-disc pl-5">{v.map((x, i) => <li key={`${keyPrefix}${i}`}>{formatScalar(x)}</li>)}</ul>;
  }
  if (typeof v === 'object') {
    return (
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
        {Object.entries(v).map(([k, val]) => (
          <div key={k} className="flex gap-2">
            <dt className="text-slate-500 min-w-[140px]">{humanize(k)}:</dt>
            <dd className="text-slate-800 break-words">
              {(typeof val === 'object' && val !== null) ? renderValue(val, `${keyPrefix}${k}.`) : formatScalar(val)}
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span>{formatScalar(v)}</span>;
}

function formatScalar(v) {
  if (v === null || v === undefined || v === '') return <span className="text-slate-400">—</span>;
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (typeof v === 'number') return v.toLocaleString('es-AR');
  return String(v);
}

function humanize(k) {
  return String(k).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}
